import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AngularFirestore } from '@angular/fire/firestore';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ProviderDataValidators as Validators } from '@app/modules/data-valiidator';
import { SpinnerService } from '@app/@shared/spinner/spinner.service';
import { UserService } from '@app/services/user.service';
import { ChangeDetectorRef } from '@angular/core';
import { WellBeingService } from '@app/wellBeing/wellBeing.service';
import { QuestionType } from '@app/interface/survey';
import { WellBeingQuestionTypeEnumes } from '@shared/enums/wellBeingQuestionType';
import { ActiveStatus } from '@app/interface/common';
import { Constant } from '@app/@core';
import { StudiesService } from '@app/services/studies.service';
import { StudyModel } from '@app/interface/study.model';

@Component({
  selector: 'app-add-well-being',
  templateUrl: './addEditWellBeing.component.html',
  styleUrls: ['./addEditWellBeing.component.scss'],
})
export class AddEditWellBeingComponent implements OnInit {
  wellBeingForm: FormGroup;
  isFormSubmitted: boolean = false;
  isLoading = false;
  uploadedFiles: any[] = [];
  preview: string;
  qtypeFields: QuestionType[] = [
    { value: 'string', viewValue: WellBeingQuestionTypeEnumes.STRING },
    { value: 'scale', viewValue: WellBeingQuestionTypeEnumes.SCALE },
    { value: 'counter', viewValue: WellBeingQuestionTypeEnumes.COUNTER },
  ];
  selectedQuestionType: string;

  @Input() title_tag: string;
  @Input() formType: string;
  @Input() form_data: any;

  studyDetail: StudyModel;
  status: Array<ActiveStatus> = Constant.ActiveStatus;

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private spinnerService: SpinnerService,
    public userService: UserService,
    public wellBeingService: WellBeingService,
    private cd: ChangeDetectorRef,
    private studiesService: StudiesService,
    private afs: AngularFirestore
  ) {}

  ngOnInit() {
    this.getstudyDetail();
    this.getAddWellBeingForm();
    this.isFormTypeEdit(this.formType) && this.getActivityUUID(this.form_data['uuid']);
  }

  private getstudyDetail(studyName?: string): void {
    this.studiesService.getStudyDetailById().subscribe((study: StudyModel) => {
      this.studyDetail = study;
    });
  }

  public selectQuestionType(e: any): void {
    this.selectedQuestionType = e?.value ? e.value['viewValue'] : e;
    if (this.selectedQuestionType === WellBeingQuestionTypeEnumes.STRING) {
      this.wellBeingForm.patchValue({
        step: null,
        lowerLabel: null,
        upperLabel: null,
      });
      this.unsetWellBeingFormSteps();
      this.unsetWellBeingFormScaleLabels();
    } else if (this.selectedQuestionType === WellBeingQuestionTypeEnumes.COUNTER) {
      this.wellBeingForm.patchValue({
        step: 1,
        lowerLabel: null,
        upperLabel: null,
      });
      this.setWellBeingFormSteps();
      this.unsetWellBeingFormScaleLabels();
    } else {
      this.wellBeingForm.patchValue({
        step: 1,
      });
      this.setWellBeingFormSteps();
    }
  }

  public unsetWellBeingFormScaleLabels(): void {
    this.f.lowerLabel.setValidators(null);
    this.f.lowerLabel.updateValueAndValidity({ emitEvent: false });
    this.f.upperLabel.setValidators(null);
    this.f.upperLabel.updateValueAndValidity({ emitEvent: false });
  }

  public setWellBeingFormSteps(): void {
    this.f.step.setValidators([Validators.required()]);
    this.f.step.updateValueAndValidity({ emitEvent: false });
  }

  public unsetWellBeingFormSteps(): void {
    this.f.step.setValidators(null);
    this.f.step.updateValueAndValidity({ emitEvent: false });
  }

  public onActivityUploadIcon(event: any): void {
    const reader = new FileReader();
    const [file] = event.target.files;
    reader.readAsDataURL(file);
    reader.onload = () => {
      this.preview = reader.result as string;
      this.wellBeingForm.patchValue({
        img: reader.result,
      });
    };
  }

  public isFormTypeEdit(formType: string) {
    switch (formType) {
      case 'Edit':
        return true;
        break;
      case 'Add':
        return false;
        break;
      default:
        return false;
        break;
    }
  }

  public getActivityUUID(activityUUID: string) {
    this.userService.getDefaultActivityUUID(activityUUID);
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.wellBeingForm.controls;
  }

  public save() {
    this.isFormSubmitted = true;
    const wellBeingFormData = this.wellBeingForm.value;
    this.selectQuestionType(this.selectedQuestionType);
    if (this.wellBeingForm.valid) {
      wellBeingFormData['qtype'] = wellBeingFormData['qtype']['value'];
      this.isLoading = true;
      this.spinnerService.show();
      if (this.formType === 'Edit') {
        this.updateWellBeingActivity(wellBeingFormData);
      } else {
        this.addWellBeingActivity(wellBeingFormData);
      }
    } else {
      return;
    }
  }

  public addWellBeingActivity(activityData: any): void {
    this.wellBeingService.addWellBeingActivity(activityData, this.studyDetail).subscribe(() => {
      this.isLoading = false;
      this.isFormSubmitted = false;
      this.activeModal.close(true);
      this.spinnerService.hide();
    });
  }

  public updateWellBeingActivity(activityData: any): void {
    this.wellBeingService.updateWellBeingActivity(activityData).subscribe(() => {
      this.isLoading = false;
      this.isFormSubmitted = false;
      this.activeModal.close(true);
      this.spinnerService.hide();
    });
  }

  public setQTypeValue(status: string) {
    let index = this.qtypeFields.map((item: any) => item.value).indexOf(status);
    this.selectedQuestionType = this.qtypeFields[index]['viewValue'];
  }

  public setQTypeFormValue(status: any): any {
    let index = this.qtypeFields.map((item: any) => item.value).indexOf(status);
    this.selectedQuestionType = this.qtypeFields[index]['viewValue'];
    return this.qtypeFields[index];
  }

  public getAddWellBeingForm() {
    this.isFormTypeEdit(this.formType) && this.setQTypeValue(this.form_data['qtype']);
    this.preview = this.isFormTypeEdit(this.formType) ? this.form_data['img'] : null;
    this.wellBeingForm = this.formBuilder.group({
      uuid: [
        this.isFormTypeEdit(this.formType) ? this.form_data['uuid'] : this.afs.createId(),
        [Validators.required()],
      ],
      name: [this.isFormTypeEdit(this.formType) ? this.form_data['name'] : '', [Validators.required()]],
      img: [this.isFormTypeEdit(this.formType) ? this.form_data['img'] : null, [Validators.required()]],
      description: [this.isFormTypeEdit(this.formType) ? this.form_data['description'] : '', []],
      unit: [this.isFormTypeEdit(this.formType) ? this.form_data['unit'] : '', []],
      position: [this.isFormTypeEdit(this.formType) ? this.form_data['position'] : '', [Validators.required()]],
      qtype: [
        this.isFormTypeEdit(this.formType) ? this.setQTypeFormValue(this.form_data['qtype']) : null,
        [Validators.required()],
      ],
      lowerLabel: [this.isFormTypeEdit(this.formType) ? this.form_data['lowerLabel'] : '', [Validators.required()]],
      upperLabel: [this.isFormTypeEdit(this.formType) ? this.form_data['upperLabel'] : '', [Validators.required()]],
      min: [this.isFormTypeEdit(this.formType) ? this.form_data['min'] : '', [Validators.required()]],
      max: [this.isFormTypeEdit(this.formType) ? this.form_data['max'] : '', [Validators.required()]],
      step: [this.isFormTypeEdit(this.formType) ? this.form_data['step'] : null, [Validators.required()]],
      isActive: [this.isFormTypeEdit(this.formType) ? this.form_data['isActive'] : false],
    });
  }

  private getRouteParams(): void {
    this.studiesService.routeParams$.subscribe((params) => {
      if (params) {
        this.getstudyDetail(params.name);
        this.getAddWellBeingForm();
        this.isFormTypeEdit(this.formType) && this.getActivityUUID(this.form_data['uuid']);
      }
    });
  }
}
